import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon, F0TagStatus } from '@factorialco/f0-react'
import { Ai, Pig, Present } from '@factorialco/f0-react/icons/app'
import { GetUpsellDiscountsDocument, ModulesDatoModule } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import CheckoutModal from 'modules/upsell/components/SelfUpsell/CheckoutModal'

const CustomAvatar = () => {
  return (
    <Box
      background="grey1000"
      border={{ all: { style: 'solid', width: 's1', color: 'grey1000' } }}
      borderRadius={{ all: 'abs012' }}
      style={{
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <F0Icon icon={Present} size="md" color="#FFFFFF" />
    </Box>
  )
}

const CustomTag = ({ text }: { text: string }) => {
  return (
    <Box
      style={{
        background:
          'linear-gradient(270deg, rgba(161, 173, 229, 0.3) 0%, rgba(229, 25, 67, 0.3) 50%, rgba(229, 86, 25, 0.3) 100%)',
        borderRadius: '20px',
        padding: '1px',
        width: 'fit-content',
      }}
    >
      <Box background="white" style={{ borderRadius: '20px' }}>
        <Box
          paddingX="s6"
          paddingY="s2"
          style={{
            borderRadius: '20px',
            background:
              'linear-gradient(270deg, rgba(161, 173, 229, 0.05) 0%, rgba(229, 25, 67, 0.05) 50%, rgba(229, 86, 25, 0.05) 100%)',
          }}
        >
          <Text size="200" weight="medium" color="grey1000">
            {text}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

type Props = {
  isHeaderCard: boolean
  item: ModulesDatoModule
}

const Card = ({ item, isHeaderCard }: Props) => {
  const navigate = useNavigate()
  const { getDetailPath } = useMarketplaceModulesPath()
  const { canSelfUpsell } = useCurrentSession()
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)
  const { data: discountsData } = useQuery(GetUpsellDiscountsDocument)
  const {
    translatedName: title,
    shortDescription: description,
    slug,
    chargebeeModuleName: moduleName,
  } = item

  const handleClick = () => {
    navigate(getDetailPath(slug, 'upsell_origin=black_friday_promo'))
  }

  const handleCheckoutModal = () => {
    if (moduleName && canSelfUpsell) {
      setIsCheckoutModalOpen(true)
    } else {
      handleClick()
    }
  }

  const hasDiscount = () => {
    const data = discountsData?.upsell?.upsellDiscountsConnection?.nodes
    const discount = data?.find((discount_item) => discount_item.module === moduleName)
    return discount
  }

  return (
    <>
      <Box
        background="white"
        borderRadius={{ all: 'abs016' }}
        style={{ minWidth: '334px', width: '334px' }}
        onClick={isHeaderCard ? undefined : handleClick}
      >
        <Box padding="s16" gap="s12" style={{ flex: '1' }}>
          <CustomAvatar />
          <Box gap="s2">
            <Text.Normal color="grey1000" weight="semibold">
              {title}
            </Text.Normal>
            <Box
              style={{
                height: '60px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
              }}
            >
              <Text.Normal size="200" color="grey800">
                {description}
              </Text.Normal>
            </Box>
          </Box>
          <Box gap="s6">
            <Box flexDirection="row" gap="s4" style={{ width: 'fit-content' }}>
              <F0Icon icon={Ai} size="md" color="#515164" />
              <CustomTag
                text={i18n.t('marketplace.black_friday_promo.marketplace_list.card_tags.includes')}
              />
            </Box>
            {hasDiscount() && (
              <Box flexDirection="row" gap="s4" style={{ width: 'fit-content' }}>
                <F0Icon icon={Pig} size="md" color="#515164" />
                <F0TagStatus
                  text={i18n.t('marketplace.revamp.list.discount', {
                    percentage: Math.floor(hasDiscount()?.percentage || 0),
                  })}
                  variant="warning"
                />
              </Box>
            )}
          </Box>
        </Box>
        {isHeaderCard && (
          <Box
            paddingX="s16"
            paddingY="s12"
            flexDirection="row"
            gap="s4"
            justifyContent="spaceBetween"
            border={{ top: { style: 'solid', width: 's1', color: 'grey200' } }}
          >
            <F0Button
              label={i18n.t('upselling_kit.button.learn_more')}
              variant="outline"
              onClick={handleClick}
            />
            <F0Button
              label={i18n.t('marketplace.revamp.get_it_now')}
              variant="default"
              onClick={handleCheckoutModal}
            />
          </Box>
        )}
      </Box>
      {isValidModule(moduleName) && (
        <CheckoutModal
          isOpen={isCheckoutModalOpen}
          onClose={() => setIsCheckoutModalOpen(false)}
          moduleName={moduleName}
          moduleSlug={slug}
          from="black_friday_promo"
        />
      )}
    </>
  )
}

export default Card
